import React, { useState, useEffect } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Label } from '../../components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Bell, Mail, Smartphone, MessageCircle, 
  Settings, Save, RefreshCw, Eye, Edit, AlertTriangle,
  FileText, Truck, CreditCard, Users, Code
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  SYSTEME: Settings,
  DEVIS: FileText,
  LOGISTIQUE: Truck,
  TERRAIN: Users,
  FINANCE: CreditCard
};

const NotificationTemplatesAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [groupedTemplates, setGroupedTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewChannel, setPreviewChannel] = useState('email');
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/notification-preferences/admin/templates`, {});
      
      setTemplates(response.data.templates || []);
      setGroupedTemplates(response.data.grouped_by_category || {});
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les templates",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      name: template.name,
      description: template.description,
      in_app_message: template.in_app_message,
      email_subject: template.email_subject,
      email_body_html: template.email_body_html,
      sms_message: template.sms_message,
      whatsapp_message: template.whatsapp_message,
      is_active: template.is_active,
      default_channels: template.default_channels
    });
    setEditDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${BACKEND_URL}/api/notification-preferences/admin/templates/${selectedTemplate.id}`,
        editForm,
        {}
      );
      
      toast({
        title: "Sauvegardé",
        description: "Template mis à jour avec succès"
      });
      
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le template",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const previewTemplate = async (template, channel = 'email') => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/notification-preferences/admin/templates/${template.id}/preview?channel=${channel}`,
        {},
        {}
      );
      
      setPreviewContent(response.data);
      setPreviewChannel(channel);
      setSelectedTemplate(template);
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Error previewing template:', error);
      toast({
        title: "Erreur",
        description: "Impossible de prévisualiser",
        variant: "destructive"
      });
    }
  };

  const resetTemplates = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les templates ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/notification-preferences/admin/templates/reset`,
        {},
        {}
      );
      
      toast({
        title: "Réinitialisé",
        description: "Tous les templates ont été réinitialisés"
      });
      
      fetchTemplates();
    } catch (error) {
      console.error('Error resetting templates:', error);
      toast({
        title: "Erreur",
        description: "Impossible de réinitialiser",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Templates de Notifications</h1>
          <p className="text-gray-500">Personnalisez les messages envoyés aux utilisateurs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTemplates}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button variant="destructive" onClick={resetTemplates}>
            Réinitialiser
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {Object.entries(groupedTemplates).map(([category, catTemplates]) => {
          const Icon = CATEGORY_ICONS[category] || Bell;
          const activeCount = catTemplates.filter(t => t.is_active).length;
          
          return (
            <Card key={category}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{catTemplates.length}</p>
                    <p className="text-sm text-gray-500">{category}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">{activeCount} actifs</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Templates List */}
      <Card>
        <CardHeader>
          <CardTitle>Tous les Templates</CardTitle>
          <CardDescription>
            Cliquez sur un template pour le modifier
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={Object.keys(groupedTemplates)[0]} className="w-full">
            <TabsList className="w-full flex flex-wrap h-auto gap-1 mb-4">
              {Object.keys(groupedTemplates).map(category => {
                const Icon = CATEGORY_ICONS[category] || Bell;
                return (
                  <TabsTrigger key={category} value={category} className="flex items-center gap-1">
                    <Icon className="h-4 w-4" />
                    {category}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
              <TabsContent key={category} value={category}>
                <div className="space-y-2">
                  {categoryTemplates.map(template => (
                    <div 
                      key={template.id}
                      className={`p-4 border rounded-lg hover:bg-gray-50 transition-colors ${!template.is_active ? 'opacity-50 bg-gray-100' : ''}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{template.name}</p>
                            {!template.is_active && (
                              <Badge variant="secondary">Désactivé</Badge>
                            )}
                            {template.is_mandatory && (
                              <Badge variant="outline">Obligatoire</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {template.target_roles?.map(role => (
                              <Badge key={role} variant="secondary" className="text-xs">
                                {role}
                              </Badge>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            <span className="text-xs text-gray-400">Canaux par défaut:</span>
                            {template.default_channels?.map(channel => (
                              <Badge key={channel} variant="outline" className="text-xs">
                                {channel}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => previewTemplate(template, 'email')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openEditDialog(template)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le template</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.name} - {selectedTemplate?.event_type}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom</Label>
                <Input 
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-4">
                <Label>Actif</Label>
                <Switch 
                  checked={editForm.is_active ?? true}
                  onCheckedChange={(checked) => setEditForm({...editForm, is_active: checked})}
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input 
                value={editForm.description || ''}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
              />
            </div>

            <Tabs defaultValue="in_app">
              <TabsList>
                <TabsTrigger value="in_app">
                  <Bell className="h-4 w-4 mr-1" /> In-App
                </TabsTrigger>
                <TabsTrigger value="email">
                  <Mail className="h-4 w-4 mr-1" /> Email
                </TabsTrigger>
                <TabsTrigger value="sms">
                  <Smartphone className="h-4 w-4 mr-1" /> SMS
                </TabsTrigger>
                <TabsTrigger value="whatsapp">
                  <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                </TabsTrigger>
              </TabsList>

              <TabsContent value="in_app" className="space-y-2">
                <Label>Message In-App</Label>
                <Input 
                  value={editForm.in_app_message || ''}
                  onChange={(e) => setEditForm({...editForm, in_app_message: e.target.value})}
                />
              </TabsContent>

              <TabsContent value="email" className="space-y-2">
                <div>
                  <Label>Sujet de l'email</Label>
                  <Input 
                    value={editForm.email_subject || ''}
                    onChange={(e) => setEditForm({...editForm, email_subject: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Corps de l'email (HTML)</Label>
                  <Textarea 
                    className="font-mono text-sm min-h-[200px]"
                    value={editForm.email_body_html || ''}
                    onChange={(e) => setEditForm({...editForm, email_body_html: e.target.value})}
                  />
                </div>
              </TabsContent>

              <TabsContent value="sms" className="space-y-2">
                <Label>Message SMS (160 caractères max)</Label>
                <Textarea 
                  value={editForm.sms_message || ''}
                  onChange={(e) => setEditForm({...editForm, sms_message: e.target.value})}
                  maxLength={160}
                />
                <p className="text-xs text-gray-500">
                  {editForm.sms_message?.length || 0}/160 caractères
                </p>
              </TabsContent>

              <TabsContent value="whatsapp" className="space-y-2">
                <Label>Message WhatsApp</Label>
                <Textarea 
                  value={editForm.whatsapp_message || ''}
                  onChange={(e) => setEditForm({...editForm, whatsapp_message: e.target.value})}
                />
              </TabsContent>
            </Tabs>

            <div className="bg-gray-50 p-4 rounded-lg">
              <Label className="flex items-center gap-2">
                <Code className="h-4 w-4" />
                Variables disponibles
              </Label>
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedTemplate?.available_variables?.map(v => (
                  <code key={v} className="px-2 py-1 bg-gray-200 rounded text-xs">
                    {`{${v}}`}
                  </code>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveTemplate} disabled={saving}>
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Prévisualisation</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.name} - Canal: {previewChannel}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Tabs value={previewChannel} onValueChange={(v) => previewTemplate(selectedTemplate, v)}>
              <TabsList>
                <TabsTrigger value="in_app">In-App</TabsTrigger>
                <TabsTrigger value="email">Email</TabsTrigger>
                <TabsTrigger value="sms">SMS</TabsTrigger>
                <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
              </TabsList>
            </Tabs>

            {previewContent && (
              <div className="border rounded-lg p-4 bg-white">
                {previewChannel === 'email' && previewContent.subject && (
                  <div className="mb-4 pb-4 border-b">
                    <p className="text-sm text-gray-500">Sujet:</p>
                    <p className="font-medium">{previewContent.subject}</p>
                  </div>
                )}
                {previewChannel === 'email' ? (
                  <div 
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent.content) }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap">
                    {previewContent.content}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationTemplatesAdmin;
